import React, { useEffect, useState } from 'react';
import { getDownloadURL, getStorage, ref as storageRef } from 'firebase/storage';

export const photoCategoryLabel = {
  before: 'Before work',
  after: 'After work',
  equipment: 'Installed equipment',
  serial: 'Serial number',
  asset_tag: 'Asset tag',
};

function photoErrorMessage(error) {
  const code = error?.code || 'unknown';
  if (code === 'storage/unauthorized') {
    return 'Access denied by Firebase Storage (storage/unauthorized). Check client evidence read permissions.';
  }
  if (code === 'storage/object-not-found') {
    return 'Image not found in Firebase Storage (storage/object-not-found). Check the registered photo path and bucket.';
  }
  if (code === 'storage/bucket-not-found') {
    return 'Storage bucket not found (storage/bucket-not-found). Check the Firebase Storage configuration.';
  }
  return `Could not retrieve this image (${code}).`;
}

export default function ServiceEvidenceGallery({ photos = [] }) {
  const [results, setResults] = useState({});
  const photoList = Array.isArray(photos) ? photos : [];
  const pathsKey = photoList.map(photo => photo?.path || '').join('|');

  useEffect(() => {
    let active = true;
    setResults({});
    if (!pathsKey) return () => { active = false; };

    const storage = getStorage();
    for (const photo of photoList) {
      const path = photo?.path;
      if (typeof path !== 'string' || !path) continue;
      getDownloadURL(storageRef(storage, path))
        .then(url => {
          if (active) setResults(previous => ({ ...previous, [path]: { url } }));
        })
        .catch(error => {
          if (active) setResults(previous => ({ ...previous, [path]: { error: photoErrorMessage(error) } }));
        });
    }
    return () => { active = false; };
  }, [pathsKey]);

  if (!photoList.length) return (
    <p className="text-sm text-gray-700">No photographs are attached to this approved report.</p>
  );

  return (
    <section className="space-y-3" aria-label="Approved service evidence photographs">
      <h4 className="font-semibold text-[#111111]">Work photographs ({photoList.length})</h4>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {photoList.map((photo, i) => {
          const path = photo?.path;
          const result = results[path] || {};
          const label = photoCategoryLabel[photo.category] || 'Service photo';
          return (
            <figure key={path || `missing-${i}`} className="rounded-xl border border-gray-200 bg-white p-3">
              {result.url ? (
                <a href={result.url} target="_blank" rel="noopener noreferrer">
                  <img
                    src={result.url}
                    alt={`${label} ${i + 1}`}
                    className="w-full h-48 object-contain bg-gray-50 rounded-lg"
                    onError={() => setResults(previous => ({
                      ...previous,
                      [path]: { error: 'The download URL was retrieved, but the browser could not display the image.' },
                    }))}
                  />
                </a>
              ) : result.error || !path ? (
                <div role="alert" className="min-h-48 bg-red-50 text-red-900 rounded-lg p-4 flex items-center text-sm">
                  {result.error || 'Missing registered Storage path for this photo.'}
                </div>
              ) : (
                <div className="h-48 bg-gray-50 rounded-lg grid place-items-center text-sm text-gray-900">Loading photo…</div>
              )}
              <figcaption className="mt-2 text-sm text-[#111111]">
                <strong>{label}</strong>
                {photo.caption && <span className="block mt-1">{photo.caption}</span>}
              </figcaption>
            </figure>
          );
        })}
      </div>
    </section>
  );
}
